import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useTransform,
  MotionValue,
} from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
import { useModalActions } from '@/components/Modals/useModalActions';
import { ORDER, MODAL_KEY } from '@/constants';
import { BannerProps } from '@/pages/container/banner/banner';
import useLocalCart from '@/hooks/useLocalCart';
import cntl from 'cntl';
import useCatalog from '@/hooks/useCatalog';

export interface BannerCTAProps {
  id?: string; // Received from Banner
  bannerVariant: BannerProps['variant'];
  disabled?: boolean;
  hidden?: boolean;
}

export const BannerCTA: React.FC<
  BannerCTAProps & { scrollProgress: MotionValue<number> }
> = ({
  bannerVariant,
  id,
  disabled = false,
  hidden = false,
  scrollProgress,
}) => {
  const navigate = useNavigate();
  const { openModal } = useModalActions(MODAL_KEY.R_DRAWER);
  // Pulling DATA from the Boss (Shopify)
  const { totalItems } = useLocalCart();
  const { isLoading } = useCatalog();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isOrderPage = location.pathname.includes('/order');

  const hasItems = totalItems > 0;
  const curStep = searchParams.get('step');
  const hideNavigationBTN =
    (!hasItems && isOrderPage) ||
    (isOrderPage && (curStep === 'customize_box' || curStep === 'choose_type'));
  const backgroundColor = useTransform(
    scrollProgress!,
    [0, 1],
    bannerVariant === 'transparent'
      ? ['#fac44700', '#fac547']
      : bannerVariant === 'frost'
        ? ['#7A936E', '#7A936E']
        : bannerVariant === 'primary' || bannerVariant === 'sage'
          ? ['#FDF7F1', '#FDF7F1']
          : bannerVariant === 'cream'
            ? ['#3d2012', '#3d2012']
            : ['currentColor', 'currentColor'],
  );

  const color = useTransform(
    scrollProgress!,
    [0, 1],
    bannerVariant === 'transparent'
      ? ['oklch(28% 0.05 45) l c h / 0.9', '#3d2012']
      : bannerVariant === 'frost' || bannerVariant === 'cream'
        ? ['#FDF7F1', '#FDF7F1']
        : bannerVariant === 'primary' || bannerVariant === 'sage'
          ? ['#3d2012', '#3d2012']
          : ['currentColor', 'currentColor'],
  );
  const handleClick = () => {
    if (hasItems) {
      openModal();
    } else {
      navigate(`${ORDER}?step=menu`);
    }
  };

  const ContainerCN = (isHidden: boolean) =>
    cntl`relative hidden md:flex items-center justify-end min-w-0 h-full min-w-[100px] sm:min-w-[140px] ${isHidden ? 'hidden' : ''}`;

  const BtnCN = cntl`w-full sm:w-auto flex items-center justify-center px-4 sm:px-8 py-2 rounded-full shadow-lg active:scale-95 overflow-hidden relative hover:cursor-pointer hover:scale-105 cursor-pointer disabled:cursor-not-allowed
  disabled:bg-gray-300 disabled:text-gray-500
  outline-none focus:outline-none`;

  return (
    <motion.div
      id={id}
      initial={false}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      // CHANGED: Use flexible height and min-width for mobile finger-tap targets
      className={ContainerCN(hidden || hideNavigationBTN)}
    >
      <div className="relative w-full flex justify-end">
        {/* 1. Improved Notification Badge */}
        <AnimatePresence>
          {hasItems && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              // CHANGED: Positioned relative to button edge for better mobile alignment
              className="absolute -top-1 -right-1 w-6 h-6 bg-brand-error rounded-full flex items-center justify-center text-[10px] text-white z-30 font-[500] font-display shadow-md border-2 border-white"
            >
              <span className="translate-y-[1px]">{totalItems}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          onClick={handleClick}
          style={{
            backgroundColor,
            color,
          }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          role="button"
          aria-label={hasItems ? 'View Cart' : 'Order Now'}
          className={BtnCN}
          disabled={isLoading || disabled}
        >
          <AnimatePresence mode="wait" initial={false}>
            {hasItems ? (
              <motion.div
                key="view-bag"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="flex items-center gap-2"
              >
                <ShoppingBag className="w-4 h-4" />
                <span className="font-[700] font-display text-xs sm:text-sm whitespace-nowrap uppercase tracking-widest translate-y-[1px]">
                  View Bag
                </span>
              </motion.div>
            ) : isLoading ? (
              <motion.svg
                className="w-5 h-5 mr-2 text-white animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <motion.circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke={color}
                  strokeWidth="4"
                />
                <motion.path
                  className="opacity-75"
                  fill={color}
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </motion.svg>
            ) : (
              <motion.span
                key="order-now"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="font-[700] font-display text-xs sm:text-sm uppercase tracking-widest translate-y-[1px]"
              >
                Order Now
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </motion.div>
  );
};
